import {useMemo, useState} from 'react';
import {useFieldArray, useFormContext} from 'react-hook-form';
import AppConfirmationDialog from '../../../../../../../components/common/AppConfirmationDialog';
import AppMenu from '../../../../../../../components/common/AppMenu';
import AppButton from '../../../../../../../components/common/AppButton';
import {SVG} from '../../../../../../../constants/assetLists';
import {useSnackbar} from '../../../../../../../context/SnackbarContextProvider';
import {useOnBoardingContext} from '../../../../../../../context/onBoarding/OnBoardingContextProvider';
import {PERMISSIONS_LEVELS} from '../../../data/permissionsList';
import {NetworkDetailsFormValues, NetworkNodeFieldPath} from '../../../types';
import Switcher from '../../Switcher';
import {getManualRoles, getTemplateRoles} from './defaultRoles';
import {
  cloneRolesFromSource,
  collectRoleSourceOptions,
} from './roleCopyUtils';
import {canReplaceRoles} from './roleReferenceUtils';
import OnboardingValidationBadge from '../../OnboardingValidationBadge';
import { get } from 'react-hook-form';
type Props = {
  selectedFieldPath: NetworkNodeFieldPath;
};

function RolesHeader({selectedFieldPath}: Props) {
  const [showPermissionLevels, setShowPermissionLevels] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedSourceName, setSelectedSourceName] = useState<string | null>(
    null,
  );
  const [pendingSourcePath, setPendingSourcePath] =
    useState<NetworkNodeFieldPath | null>(null);
  const [showCopyConfirmation, setShowCopyConfirmation] = useState(false);

  const {control, setValue, watch, getValues,formState: { errors },} =
    useFormContext<NetworkDetailsFormValues>();
    const roles = watch(`${selectedFieldPath}.roles`) ?? [];
    const rolesError = get(
      errors,
      `${selectedFieldPath}.roles`
    );
    const showRolesValidation =
      roles.length === 0 ||
      !!rolesError;
  const {replace} = useFieldArray({
    control,
    name: `${selectedFieldPath}.roles`,
    keyName: 'id',
  });
  const {getDefaultSelectedNode, setDefaultSelectedNodeForPath, allRoleTypes} =
    useOnBoardingContext();
  const defaultSelectedNode = getDefaultSelectedNode(selectedFieldPath);
  const {showSnackbar} = useSnackbar();
  const [pendingRoleMode, setPendingRoleMode] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const network = watch('network') ?? [];
  const departments = watch(`${selectedFieldPath}.departments`) ?? [];

  const blockReplaceIfRolesInUse = (): boolean => {
    const result = canReplaceRoles(
      network,
      selectedFieldPath,
      allRoleTypes?.data,
    );
    if (!result.allowed) {
      showSnackbar({
        message: result.message,
        type: 'error',
        duration: 5000,
      });
      return true;
    }
    return false;
  };

  const menuItems = useMemo(
    () =>
      collectRoleSourceOptions(network, selectedFieldPath).map(o => ({
        title: o.title,
        value: o.value,
      })),
    [network, selectedFieldPath],
  );

  const buttonLabel = selectedSourceName
    ? `ייבוא הגדרות מ${selectedSourceName}`
    : 'ייבוא הגדרות מ...';

  const buildRolesForMode = (mode: string) =>
    mode === 'manual'
      ? getManualRoles(departments)
      : getTemplateRoles(departments, allRoleTypes?.data);

  const togglePermissionLevels = (opt: boolean) => {
    setShowPermissionLevels(opt);
  };

  const handleSwitcherChange = (id: string) => {
    if (id === defaultSelectedNode.role) return;
    if (blockReplaceIfRolesInUse()) return;

    setPendingRoleMode(id);
    setShowConfirmation(true);
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
    setPendingRoleMode(null);
  };

  const handleConfirmChange = () => {
    if (!pendingRoleMode) return;
    if (blockReplaceIfRolesInUse()) {
      handleCloseConfirmation();
      return;
    }

    const nextRoles = buildRolesForMode(pendingRoleMode);
    replace(nextRoles);
    setValue(`${selectedFieldPath}.roles`, nextRoles, {
      shouldDirty: true,
      shouldTouch: true,
    });
    setDefaultSelectedNodeForPath(selectedFieldPath, prev => ({
      ...prev,
      role: pendingRoleMode,
    }));
    setShowConfirmation(false);
    setPendingRoleMode(null);
  };

  const handleSourceSelect = (sourcePath: string) => {
    if (blockReplaceIfRolesInUse()) return;

    const sourceName =
      getValues(
        `${sourcePath as NetworkNodeFieldPath}.name` as const,
      )?.trim() || 'ללא שם';

    setSelectedSourceName(sourceName);
    setPendingSourcePath(sourcePath as NetworkNodeFieldPath);
    setShowCopyConfirmation(true);
  };

  const handleCloseCopyConfirmation = () => {
    setShowCopyConfirmation(false);
    setPendingSourcePath(null);
    setSelectedSourceName(null);
  };

  const handleConfirmCopy = () => {
    if (!pendingSourcePath) return;
    if (blockReplaceIfRolesInUse()) {
      handleCloseCopyConfirmation();
      return;
    }

    const sourceRoles =
      getValues(`${pendingSourcePath}.roles` as const) ?? [];
    const sourceDepartments =
      getValues(`${pendingSourcePath}.departments` as const) ?? [];
    const targetDepartments =
      getValues(`${selectedFieldPath}.departments` as const) ?? [];
    const cloned = cloneRolesFromSource(
      sourceRoles,
      sourceDepartments,
      targetDepartments,
    );

    replace(cloned);
    setValue(`${selectedFieldPath}.roles`, cloned, {
      shouldDirty: true,
      shouldTouch: true,
    });
    setDefaultSelectedNodeForPath(selectedFieldPath, prev => ({
      ...prev,
      role: 'manual',
    }));
    setShowCopyConfirmation(false);
    setPendingSourcePath(null);
  };

  const subtitle =
    pendingRoleMode === 'manual' ? (
      <p>
        מעבר <span className="font-bold">ידנית</span> יאפס את ההגדרות בתבנית
        שהוגדרו. האם להמשיך?
      </p>
    ) : (
      <p>
        מעבר <span className="font-bold">לתבנית</span> יאפס את ההגדרות האישיות
        שהוגדרו. האם להמשיך?
      </p>
    );

  const copySubtitle = (
    <p>
      העתקת תפקידים מ-
      <span className="font-bold">{selectedSourceName || 'ללא שם'}</span> תחליף
      את התפקידים הנוכחיים. תפקידים ישויכו למחלקות עם שם תואם; תפקידים ללא
      התאמה יישארו ללא מחלקה. האם להמשיך?
    </p>
  );

  return (
    <>
      <div className="flex flex-col pb-2">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
          <div>
            <div className="flex items-center gap-3">
              <p className="m-0 text-xl text-deepAqua font-bold">
                תפקידים והרשאות
              </p>
             <OnboardingValidationBadge show={showRolesValidation}  />
            </div>

            <p className="m-0 mt-2 text-base text-darkerGray">
              הגדירו את תפקידי הארגון והקצו לכל תפקיד רמת הרשאה מתאימה. <br />{' '}
              לחצו על סימן ה-X בטבלה כדי להפעיל הרשאה. <br /> השתמשו בתבנית
              המוכנה כנקודת התחלה והתאימו אותה לצרכיכם.
            </p>
          </div>
          <div>
            <Switcher
              active={defaultSelectedNode.role}
              onChange={handleSwitcherChange}
            />
          </div>
          <div className="flex justify-center md:justify-end">
            <AppMenu
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl)}
              onOpen={e => setMenuAnchorEl(e.currentTarget as HTMLElement)}
              onClose={() => setMenuAnchorEl(null)}
              items={menuItems}
              disableMenuIcon={menuItems.length === 0}
              onItemClick={handleSourceSelect}
              minWidth={260}
              menuIcon={
                <AppButton
                  variant="primary"
                  disabled={menuItems.length === 0}
                  iconEnd={
                    <img
                      src={SVG.dropdownWhiteIcon}
                      alt="icon"
                      className="w-[12px] h-[6px] object-contain shrink-0"
                    />
                  }>
                  {buttonLabel}
                </AppButton>
              }
            />
          </div>
        </div>

        <div className="mt-1 flex flex-col gap-2.5">
          <div className="flex justify-end gap-12">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <img src={SVG.tickGreenIcon} alt="" />
                <p>הרשאה פעילה</p>
              </div>
              <div className="flex items-center gap-2">
                <img src={SVG.closeDarkIcon} alt="" />
                <p>הרשאה כבויה</p>
              </div>
            </div>
            <button
              className="py-1 px-2 bg-icyBlue rounded-md flex items-center gap-2"
              onClick={() => togglePermissionLevels(true)}
              type="button">
              <img
                src={SVG.infoBlueIcon}
                alt="info"
                className="w-5 h-5 object-contain"
              />
              <p className="m-0 text-base text-tealBlue underline">
                רשימת ההרשאות לפי דרג הרשאה
              </p>
            </button>
          </div>

          {showPermissionLevels && (
            <div className="px-4 py-1 border border-[#E9FAFF] bg-[#F0FCFF] rounded-tr-md rounded-br-md rounded-bl-md shadow-md relative">
              {PERMISSIONS_LEVELS.map((level, idx) => (
                <div
                  key={level.id}
                  className={`py-1 flex items-center gap-2 border-b border-gray70 ${idx === PERMISSIONS_LEVELS.length - 1 ? 'border-b-0' : ''}`}>
                  <div className="w-[122px]">
                    <p className="m-0 text-base font-bold text-darkBlue">
                      {level.name}
                    </p>
                  </div>

                  <p className="m-0 text-base text-black">{level.desc}</p>
                </div>
              ))}

              <button
                className="absolute top-1 left-2 w-6 h-6 flex justify-center items-center"
                onClick={() => togglePermissionLevels(false)}
                type="button">
                <img
                  src={SVG.closeDarkIcon}
                  alt=""
                  className="w-[11px] h-[11px] object-contain"
                />
              </button>
            </div>
          )}
        </div>
      </div>

      <AppConfirmationDialog
        open={showConfirmation}
        onClose={handleCloseConfirmation}
        onConfirm={handleConfirmChange}
        icon={
          <img src={SVG.infoBlueIcon} alt="" className="w-[48px] h-[48px]" />
        }
        title="שימו לב"
        subTitle={subtitle}
        confirmBtnText="כן"
        cancelBtnText="לא"
        cancelBtnTextClasses="!text-raspberryRed"
        bordered={false}
      />

      <AppConfirmationDialog
        open={showCopyConfirmation}
        onClose={handleCloseCopyConfirmation}
        onConfirm={handleConfirmCopy}
        icon={
          <img src={SVG.infoBlueIcon} alt="" className="w-[48px] h-[48px]" />
        }
        title="שימו לב"
        subTitle={copySubtitle}
        confirmBtnText="כן"
        cancelBtnText="לא"
        cancelBtnTextClasses="!text-raspberryRed"
        bordered={false}
      />
    </>
  );
}

export default RolesHeader;
